import { useEffect, useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { SupportContext } from './support-context';
import { useSupportSession } from '../hooks/use-support-session';
import type { SupportProviderProps } from '../types/support.types';
import { buildSupportWhatsAppUrl } from '../utils/support-whatsapp.util';

/** Shares Messenger state and accessible failure recovery across navigation. */
export function SupportProvider({ children }: SupportProviderProps) {
  const support = useSupportSession();
  const { t } = useI18n();
  const [fallbackOpen, setFallbackOpen] = useState(false);
  useEffect(() => {
    if (fallbackOpen && support.status === 'ready' && support.open()) {
      setFallbackOpen(false);
    }
  }, [fallbackOpen, support]);
  const message = support.user
    ? t('sidebar.support.message', {
        userName: formatFullName(support.user.firstName, support.user.lastName),
        userId: support.user.id,
      })
    : t('sidebar.support.message.default');

  return (
    <SupportContext.Provider
      value={{
        status: support.status,
        unreadCount: support.unreadCount,
        isOpen: support.isOpen,
        open: () => {
          if (!support.open()) setFallbackOpen(true);
        },
        shutdown: support.shutdown,
      }}
    >
      {children}
      <Modal
        compact
        isOpen={fallbackOpen}
        onClose={() => setFallbackOpen(false)}
        header={<Text weight="semibold">{t('sidebar.support.label')}</Text>}
        body={
          <div className="flex flex-col gap-4">
            <div role="status">
              <Text size="small">
                {t(support.loading ? 'support.loading' : 'support.unavailable')}
              </Text>
            </div>
            <a
              className="font-medium text-whatsapp-700 underline underline-offset-4"
              href={buildSupportWhatsAppUrl(message)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('support.whatsapp')}
            </a>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2 p-4">
            <Button appearance="ghost" onClick={() => setFallbackOpen(false)}>
              {t('shared.actions.close')}
            </Button>
            <Button onClick={support.retry} disabled={support.loading}>
              {t('support.retry')}
            </Button>
          </div>
        }
      />
    </SupportContext.Provider>
  );
}
